import { CommandModel } from "@/app/entities";
import { AddComandeDTO, BoucleGenrateDTO } from "@/app/entities/commande.model";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useToast } from "../toast";
import { useCommandeStore } from "@/context";
import { UserTypesCodes, useUsers } from "@/composables/users";
import useApi from "@/services/use.api";
import { ObjectToQueryParams } from "@/utils";
import { string } from "yup";

const { useGetApi, usePostApi, usePutApi } = useApi();

export interface FetchCommandFilterParams {
  page?: number;
  sort?: string;
  fournisseur?: string;
  statut?: string;
  dateCreation?: string;
  dateFabrication?: string;
  userType?: string;
}

export const useCommande = () => {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const {
    filterForm,
    sort,
    setCommandes,
    getCommandeByReference,
    setFilterForm,
    setPaginatedItems,
  } = useCommandeStore();
  const navigate = useNavigate();

  const commandes = useCommandeStore((state) => state.paginatedItems.content);

  const fetchCommandes = async (params?: FetchCommandFilterParams) => {
    // if (commandes.length > 0 && !reset) return;
    setLoading(true);
    const param = ObjectToQueryParams({
      page: params?.page || 0,
      sort:
        params?.sort || sort
          ? `${sort?.sortKey},${sort?.direction}`
          : undefined,
      fournisseur: params?.fournisseur,
      statut: params?.statut || filterForm.status,
      dateCreation: params?.dateCreation || filterForm.creationDate,
      dateFabrication: params?.dateFabrication || filterForm.dfs,
    });
    const type = params?.userType || UserTypesCodes.Technique;
    const res = await useGetApi<{
      content: CommandModel[];
      empty: boolean;
      first: boolean;
      last: boolean;
      number: number;
      numberOfElements: number;
      pageable: object;
      size: number;
      sort: object;
      totalElements: number;
      totalPages: number;
    }>(`/auth/${type}/commande${param}`);
    const { data } = res ?? {};

    setPaginatedItems({
      content: data?.content || [],
      empty: data?.empty || true,
      first: data?.first || true,
      last: data?.last || true,
      number: data?.number || 0,
      numberOfElements: data?.numberOfElements || 0,
      pageable: data?.pageable || {},
      size: data?.size || 0,
      sort: data?.sort || {},
      totalElements: data?.totalElements || 0,
      totalPages: data?.totalPages || 0,
    });

    if (data?.content && Array.isArray(data.content)) {
      setCommandes(data?.content ?? []);
    }
    setLoading(false);
  };

  const fetchCommandesByReference = async (
    reference: string,
    userType: string = UserTypesCodes.Fournisseur
  ): Promise<CommandModel | undefined> => {
    setLoading(true);
    const res = await useGetApi(`/auth/${userType}/commande/${reference}`);
    setLoading(false);
    const { data } = res ?? {};
    return data as CommandModel;
  };

  const saveCommande = async (params: AddComandeDTO) => {
    setLoading(true);
    const res = await usePostApi<CommandModel>(
      `/auth/technique/commande/${params.referenceCampagne}`,
      params
    );
    const { data } = res ?? {};
    setLoading(false);
    if (data) {
      setCommandes([data, ...commandes]);
      setPaginatedItems({
        ...useCommandeStore.getState().paginatedItems,
        content: [data, ...useCommandeStore.getState().paginatedItems.content],
      });
      useToast("Commande ajoute avec succès", { variant: "success" });
      navigate("/dashboard/commandes");
    }
  };

  const waitingCommandeAction = async (reference: string, accept: boolean) => {
    setLoading(true);
    const res = await usePutApi<CommandModel>(
      `/auth/admin/commande/${reference}/${accept}`,
      { reference, accept }
    );
    setLoading(false);

    const { data, success, message } = res ?? {};
    if (!success) {
      useToast(message ?? "Une erreur s'est produite", {
        variant: "error",
      });
      return;
    }

    fetchCommandes();
  };

  const generationBoucles = async ({
    reference,
    nbLot,
    debut,
  }: BoucleGenrateDTO): Promise<CommandModel | undefined> => {
    setGenerating(true);
    const res = await useGetApi(
      `/auth/fournisseur/commande/${reference}/${debut}/${nbLot}`
    );
    setGenerating(false);

    const { data, success, message } = res ?? {};
    if (!success) {
      useToast(message ?? "Une erreur s'est produite", {
        variant: "error",
      });
      return undefined;
    }

    // const { data: orderDTO } = data ?? {};
    return data as CommandModel;
  };

  const dechargerCommande = async ({
    reference,
    file,
  }: {
    reference: string;
    file: File;
  }): Promise<CommandModel | undefined> => {
    setLoading(true);
    const form = new FormData();
    form.append("reference", reference);
    form.append("fichier", file);

    const res = await usePutApi(
      `/auth/fournisseur/commande/${reference}`,
      form,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    setLoading(false);

    const { data, success, message } = res ?? {};
    if (!success) {
      useToast(message ?? "Une erreur s'est produite", {
        variant: "error",
      });
      return undefined;
    }

    // const { data: orderDTO } = data ?? {};
    useToast("La commande bien été déchargée", {
      variant: "success",
    });
    return data as CommandModel;
  };

  const downloadBlob = async (data: any, reference: string) => {
    if (!data) {
      useToast("Aucune donnée à télécharger", { variant: "error" });
      return;
    }

    const blob = new Blob([data], {
      type: "application/zip",
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `boucles-${reference}.zip`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const downloadBoucles = async (reference: string): Promise<boolean> => {
    if (!reference) {
      useToast("Référence manquante", { variant: "error" });
      return false;
    }
    setLoading(true);
    const res = await useGetApi<any>(`/auth/fournisseur/fichier/${reference}`, {
      responseType: "blob",
      headers: {
        Accept: "application/zip, application/octet-stream",
      },
    });
    const { data, success, message } = res ?? {};
    if (!success) {
      useToast(message ?? "Une erreur s'est produite", {
        variant: "error",
      });
      setLoading(false);
      return false;
    }

    await downloadBlob(data, reference);

    useToast("Fichier téléchargé avec succès", {
      variant: "success",
    });
    fetchCommandes({ userType: UserTypesCodes.Fournisseur, page: 0 });
    setLoading(false);
    return true;
  };

  const getCommandeStatusBadgeCode = (commande?: CommandModel): string => {
    return commande?.statut ?? "";
  };

  return {
    loading,
    generating,
    commandes,
    setFilterForm,
    dechargerCommande,
    fetchCommandesByReference,
    fetchCommandes,
    saveCommande,
    waitingCommandeAction,
    getCommandeByReference,
    generationBoucles,
    downloadBoucles,
    getCommandeStatusBadgeCode,
  };
};
